import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, Link } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight, Camera, Play, Image, Video, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GalleryPage = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const eventFilter = searchParams.get('event'); // Filtro per evento specifico
  
  const [showLightbox, setShowLightbox] = useState(false);
  const [currentGallery, setCurrentGallery] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [eventGalleries, setEventGalleries] = useState([]);
  const [filteredGallery, setFilteredGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('photos'); // photos, videos

  // Fetch galleries grouped by event
  const fetchGalleries = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/gallery/by-event`);
      const galleries = response.data || [];
      setEventGalleries(galleries);
      
      // Se c'è un filtro evento, trova la galleria specifica
      if (eventFilter) {
        const found = galleries.find(g => g.event_id === eventFilter);
        setFilteredGallery(found || null);
      }
    } catch (err) {
      console.error('Error fetching galleries:', err);
    } finally {
      setLoading(false);
    }
  }, [eventFilter]);

  useEffect(() => {
    fetchGalleries();
  }, [fetchGalleries]);

  const openLightbox = (gallery, index = 0) => {
    setCurrentGallery(gallery);
    setCurrentIndex(index);
    setShowLightbox(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setShowLightbox(false);
    setCurrentGallery(null);
    document.body.style.overflow = 'unset';
  };

  const nextImage = () => {
    if (currentGallery && currentGallery.photos) {
      setCurrentIndex((prev) => (prev + 1) % currentGallery.photos.length);
    }
  };

  const prevImage = () => {
    if (currentGallery && currentGallery.photos) {
      setCurrentIndex((prev) => (prev - 1 + currentGallery.photos.length) % currentGallery.photos.length);
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showLightbox) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showLightbox, currentGallery]);

  // Get photo URL
  const getPhotoUrl = (photo) => {
    if (!photo?.image_url) return '';
    return photo.image_url.startsWith('http') ? photo.image_url : `${BACKEND_URL}${photo.image_url}`;
  };

  // Count totals - usa galleria filtrata se presente
  const galleriesForCount = eventFilter && filteredGallery ? [filteredGallery] : eventGalleries;
  const totalPhotos = galleriesForCount.reduce((sum, g) => sum + (g.photos?.length || 0), 0);
  const totalVideos = galleriesForCount.reduce((sum, g) => sum + (g.videos?.length || 0), 0);

  // Gallerie da mostrare
  const galleriesToShow = eventFilter && filteredGallery ? [filteredGallery] : eventGalleries;

  const seoTitles = {
    it: 'Galleria Foto e Video | Offroad Italia',
    en: 'Photo & Video Gallery | Offroad Italia',
    de: 'Foto- & Videogalerie | Offroad Italia',
    fr: 'Galerie Photos & Vidéos | Offroad Italia',
    es: 'Galería de Fotos y Videos | Offroad Italia'
  };
  
  const seoDescriptions = {
    it: 'Guarda le foto e i video dei nostri tour offroad in moto. Tunisia, Libia, Italia, Grecia e molto altro.',
    en: 'Watch photos and videos of our offroad motorcycle tours. Tunisia, Libya, Italy, Greece and more.',
    de: 'Sehen Sie Fotos und Videos unserer Offroad-Motorradtouren. Tunesien, Libyen, Italien, Griechenland und mehr.',
    fr: 'Regardez les photos et vidéos de nos tours moto offroad. Tunisie, Libye, Italie, Grèce et plus.',
    es: 'Mira las fotos y videos de nuestros tours offroad en moto. Túnez, Libia, Italia, Grecia y más.'
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead 
        title={seoTitles[i18n.language] || seoTitles.it}
        description={seoDescriptions[i18n.language] || seoDescriptions.it}
      />
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white py-20">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          {/* Back button when filtered */}
          {eventFilter && (
            <Link 
              to="/gallery"
              className="inline-flex items-center text-blue-400 hover:text-blue-300 mb-6 transition-colors"
            >
              <ArrowLeft className="mr-2" size={20} />
              {t('gallery.backToAll')}
            </Link>
          )}
          
          <Camera className="mx-auto mb-4 text-blue-400" size={48} />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {eventFilter && filteredGallery 
              ? `Galleria: ${filteredGallery.event_title}`
              : t('gallery.title', 'Galleria')
            }
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            {eventFilter && filteredGallery
              ? (i18n.language === 'it' ? 'Foto e video di questo evento' : i18n.language === 'en' ? 'Photos and videos of this event' : i18n.language === 'de' ? 'Fotos und Videos von diesem Event' : i18n.language === 'fr' ? 'Photos et vidéos de cet événement' : 'Fotos y videos de este evento')
              : t('gallery.subtitle')
            }
          </p>
          
          {/* Stats */}
          <div className="flex justify-center gap-8 mt-8">
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-400">{totalPhotos}</p>
              <p className="text-gray-400">{t('gallery.photos')}</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-red-400">{totalVideos}</p>
              <p className="text-gray-400">{t('gallery.videos')}</p>
            </div>
            {!eventFilter && (
              <div className="text-center">
                <p className="text-3xl font-bold text-green-400">{eventGalleries.length}</p>
                <p className="text-gray-400">{t('gallery.events')}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Tabs */}
        <div className="flex justify-center gap-4 mb-8">
          <button
            onClick={() => setActiveTab('photos')}
            className={`flex items-center px-6 py-3 rounded-full font-semibold transition-all ${
              activeTab === 'photos'
                ? 'bg-blue-600 text-white shadow-lg'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Image size={20} className="mr-2" />
            {t('gallery.photos')} ({totalPhotos})
          </button>
          <button
            onClick={() => setActiveTab('videos')}
            className={`flex items-center px-6 py-3 rounded-full font-semibold transition-all ${
              activeTab === 'videos'
                ? 'bg-red-600 text-white shadow-lg'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Video size={20} className="mr-2" />
            {t('gallery.videos')} ({totalVideos})
          </button>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-500">{t('common.loading')}</p>
          </div>
        ) : eventFilter && !filteredGallery ? (
          /* Nessuna galleria per questo evento */
          <div className="text-center py-20 bg-white rounded-xl shadow">
            <Camera size={64} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700">{t('gallery.noGallery')}</h3>
            <p className="text-gray-500 mt-2">{t('gallery.comingSoon')}</p>
            <Link 
              to="/gallery"
              className="inline-flex items-center mt-6 text-blue-600 hover:text-blue-700 font-semibold"
            >
              <ArrowLeft className="mr-2" size={18} />
              {t('gallery.backToAll')}
            </Link>
          </div>
        ) : galleriesToShow.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl shadow">
            <Camera size={64} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700">{t('gallery.noPhotos')}</h3>
            <p className="text-gray-500 mt-2">{t('gallery.comingSoon')}</p>
          </div>
        ) : (
          <div className="space-y-12">
            {galleriesToShow.map((gallery) => {
              const items = activeTab === 'photos' ? gallery.photos : gallery.videos;
              if (!items || items.length === 0) return null;

              return (
                <div key={gallery.event_id} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  {/* Gallery Header */}
                  <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white p-6">
                    <h2 className="text-2xl font-bold">{gallery.event_title}</h2>
                    <p className="text-gray-300 mt-1">
                      {gallery.photos?.length || 0} foto • {gallery.videos?.length || 0} video
                    </p>
                  </div>

                  {/* Photos Grid */}
                  {activeTab === 'photos' && gallery.photos?.length > 0 && (
                    <div className="p-6">
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                        {gallery.photos.slice(0, 10).map((photo, idx) => (
                          <div
                            key={photo.id}
                            className="aspect-square cursor-pointer group relative overflow-hidden rounded-lg"
                            onClick={() => openLightbox(gallery, idx)}
                          >
                            <img
                              src={getPhotoUrl(photo)}
                              alt={photo.title || 'Foto'}
                              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                              loading="lazy"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                              <Camera className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={32} />
                            </div>
                          </div>
                        ))}
                        {gallery.photos.length > 10 && (
                          <div
                            className="aspect-square cursor-pointer bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200 transition-colors"
                            onClick={() => openLightbox(gallery, 10)}
                          >
                            <div className="text-center">
                              <p className="text-3xl font-bold text-gray-600">+{gallery.photos.length - 10}</p>
                              <p className="text-sm text-gray-500">{i18n.language === 'it' ? 'altre foto' : i18n.language === 'en' ? 'more photos' : i18n.language === 'de' ? 'weitere Fotos' : i18n.language === 'fr' ? 'autres photos' : 'más fotos'}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Videos Grid */}
                  {activeTab === 'videos' && gallery.videos?.length > 0 && (
                    <div className="p-6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                        {gallery.videos.map((video) => {
                          // Extract YouTube video ID from URL
                          const getYoutubeId = (url) => {
                            if (!url) return null;
                            const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
                            const match = url.match(regExp);
                            return (match && match[7]?.length === 11) ? match[7] : null;
                          };
                          const youtubeId = getYoutubeId(video.video_url);
                          
                          return (
                            <div key={video.id} className="group">
                              {youtubeId ? (
                                // YouTube Embedded Player
                                <div className="relative aspect-video rounded-lg overflow-hidden shadow-lg">
                                  <iframe
                                    src={`https://www.youtube.com/embed/${youtubeId}?rel=0`}
                                    title={video.title || 'Video'}
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    className="w-full h-full"
                                  />
                                </div>
                              ) : (
                                // Fallback for non-YouTube videos
                                <a
                                  href={video.video_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block"
                                >
                                  <div className="relative aspect-video rounded-lg overflow-hidden shadow-lg">
                                    <img
                                      src={video.image_url || '/images/video-placeholder.jpg'}
                                      alt={video.title || 'Video'}
                                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                    />
                                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/50 transition-colors">
                                      <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                                        <Play className="text-white ml-1" size={32} />
                                      </div>
                                    </div>
                                  </div>
                                </a>
                              )}
                              <p className="mt-3 font-medium text-gray-900 text-center">
                                {video.title || 'Video'}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {showLightbox && currentGallery && currentGallery.photos && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center">
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
          >
            <X size={32} />
          </button>

          {/* Navigation */}
          <button
            onClick={prevImage}
            className="absolute left-4 text-white hover:text-gray-300 p-2"
          >
            <ChevronLeft size={48} />
          </button>
          <button
            onClick={nextImage}
            className="absolute right-4 text-white hover:text-gray-300 p-2"
          >
            <ChevronRight size={48} />
          </button>

          {/* Image */}
          <div className="max-w-5xl max-h-[85vh] mx-4">
            <img
              src={getPhotoUrl(currentGallery.photos[currentIndex])}
              alt={currentGallery.photos[currentIndex]?.title || 'Foto'}
              className="max-w-full max-h-[85vh] object-contain"
            />
          </div>

          {/* Counter */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white bg-black/50 px-4 py-2 rounded-full">
            {currentIndex + 1} / {currentGallery.photos.length}
          </div>

          {/* Title */}
          <div className="absolute top-4 left-4 text-white">
            <h3 className="text-lg font-semibold">{currentGallery.event_title}</h3>
            <p className="text-gray-400 text-sm">
              {currentGallery.photos[currentIndex]?.title}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
